import { forwardRef } from "react";
import { cn } from "../utils/cn";

// ── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({ dim }: { dim: number }) {
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin shrink-0"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-25"
      />
      <path
        fill="currentColor"
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Controls the color scheme and fill style of the button */
  variant?: "primary" | "secondary" | "ghost" | "danger" | "success";
  /** Controls height, padding, and font size */
  size?: "sm" | "md" | "lg";
  /** Replaces content with a spinner and blocks interaction */
  isLoading?: boolean;
  /** Node rendered to the left of the label */
  leftIcon?: React.ReactNode;
  /** Node rendered to the right of the label */
  rightIcon?: React.ReactNode;
  /** Expands the button to fill its container's full width */
  fullWidth?: boolean;
  children: React.ReactNode;
}

// ── Style maps ────────────────────────────────────────────────────────────────

const BASE =
  "inline-flex items-center justify-center font-medium select-none " +
  "rounded-md transition-all duration-150 " +
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-brand-purple focus-visible:ring-offset-2 focus-visible:ring-offset-surface " +
  "disabled:cursor-not-allowed";

const VARIANTS: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: cn(
    "bg-brand-purple text-white",
    "hover:bg-brand-purple-hover",
    "active:bg-brand-purple-dark",
    "disabled:bg-brand-purple/40",
  ),
  secondary: cn(
    "bg-surface text-brand-purple border border-brand-purple",
    "dark:text-violet-400 dark:border-violet-500",
    "hover:bg-brand-purple-light dark:hover:bg-violet-950/60",
    "active:bg-brand-purple-muted dark:active:bg-violet-950",
    "disabled:border-border-default disabled:text-content-disabled",
    "dark:disabled:border-border-strong",
  ),
  ghost: cn(
    "bg-transparent text-brand-purple dark:text-violet-400",
    "hover:bg-brand-purple-light dark:hover:bg-violet-950/60",
    "active:bg-brand-purple-muted dark:active:bg-violet-950",
    "disabled:text-content-disabled",
  ),
  danger: cn(
    "bg-red-600 text-white",
    "dark:bg-red-700",
    "hover:bg-red-700 dark:hover:bg-red-800",
    "active:bg-red-800 dark:active:bg-red-900",
    "disabled:bg-red-400 dark:disabled:bg-red-900/60",
  ),
  success: cn(
    "bg-green-600 text-white",
    "dark:bg-green-700",
    "hover:bg-green-700 dark:hover:bg-green-800",
    "active:bg-green-800 dark:active:bg-green-900",
    "disabled:bg-green-400 dark:disabled:bg-green-900/60",
  ),
};

const SIZES: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-base gap-2",
  lg: "h-12 px-6 text-lg gap-2",
};

const SPINNER_DIM: Record<NonNullable<ButtonProps["size"]>, number> = {
  sm: 14,
  md: 15,
  lg: 16,
};

// ── Component ─────────────────────────────────────────────────────────────────

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={isLoading}
        className={cn(
          BASE,
          VARIANTS[variant],
          SIZES[size],
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {isLoading ? (
          <>
            <Spinner dim={SPINNER_DIM[size]} />
            <span className="opacity-60">{children}</span>
          </>
        ) : (
          <>
            {leftIcon && (
              <span className="shrink-0 inline-flex" aria-hidden="true">
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span className="shrink-0 inline-flex" aria-hidden="true">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";
